import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RefreshCw, Plus } from 'lucide-react';
import { PageHeader } from '@/components/layout';
import { Button, Card, CardContent, TableSkeleton } from '@/components/ui';
import { CursorPagination } from '@/components/ui/cursor-pagination';
import { ApiErrorDisplay } from '@/components/features/error';
import { TransactionFilters } from '@/components/features/transactions/transaction-filters';
import { TransactionTable, type TransactionSortColumn } from '@/components/features/transactions/transaction-table';
import { useAdminTransactions } from '@/hooks/use-admin-transactions';
import { useSortState } from '@/hooks/use-sort-state';
import { sortItems } from '@/lib/utils/sort';
import { filterByDateRange, filterByAmountRange, euroToCents } from '@/lib/utils/client-filters';
import type { DateRange } from '@/components/shared/date-range-picker';
import type { ListAdminTransactionsParams } from '@/types/admin-transaction';

export function TransactionsPage() {
  const { t } = useTranslation('transactions');
  const navigate = useNavigate();

  // Server-seitige Filter (resetten Cursor)
  const [filters, setFilters] = useState<ListAdminTransactionsParams>({ limit: 20 });
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const currentCursor = cursorStack.length > 0 ? cursorStack[cursorStack.length - 1] : undefined;

  // Client-seitige Filter (kein Cursor-Reset)
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();

  // Sortierung (client-seitig, Default: Datum desc)
  const { sort, toggleSort } = useSortState<TransactionSortColumn>('createdAt', 'desc');

  const { data, isLoading, error, refetch } = useAdminTransactions({
    ...filters,
    cursor: currentCursor,
  });

  // Server-Filter Handler (resettet Cursor)
  const handleFiltersChange = useCallback((newFilters: ListAdminTransactionsParams) => {
    setFilters(newFilters);
    setCursorStack([]);
  }, []);

  // Date Range Handler (kein Cursor-Reset)
  const handleDateRangeChange = useCallback((range: DateRange) => {
    setDateFrom(range.from);
    setDateTo(range.to);
  }, []);

  const handleNextPage = useCallback((cursor: string) => {
    setCursorStack((prev) => [...prev, cursor]);
  }, []);

  const handlePreviousPage = useCallback(() => {
    setCursorStack((prev) => prev.slice(0, -1));
  }, []);

  const handleRowClick = useCallback((transactionId: string, merchantId?: string) => {
    navigate(`/transactions/${transactionId}`, {
      state: merchantId ? { merchantId } : undefined,
    });
  }, [navigate]);

  // Pipeline: API-Daten → Client-Filter → Sort
  const { filteredItems, totalCount } = useMemo(() => {
    if (!data?.data) return { filteredItems: [], totalCount: 0 };
    const items = data.data;
    const total = items.length;

    let result = filterByDateRange(items, 'createdAt', dateFrom, dateTo);
    result = filterByAmountRange(result, 'grossAmount', euroToCents(amountMin), euroToCents(amountMax));
    result = sortItems(result, sort.column, sort.direction);

    return { filteredItems: result, totalCount: total };
  }, [data?.data, dateFrom, dateTo, amountMin, amountMax, sort.column, sort.direction]);

  const hasActiveClientFilters = dateFrom || dateTo || amountMin || amountMax;
  const showFilterHint = hasActiveClientFilters && filteredItems.length < totalCount;

  return (
    <div>
      <PageHeader
        title={t('list.title')}
        description={t('list.description')}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="mr-2 h-4 w-4" />
              {t('common:button.refresh')}
            </Button>
            <Button size="sm" onClick={() => navigate('/transactions/trigger')}>
              <Plus className="mr-2 h-4 w-4" />
              {t('list.newTransaction')}
            </Button>
          </div>
        }
      />

      <TransactionFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        amountMin={amountMin}
        amountMax={amountMax}
        onAmountMinChange={setAmountMin}
        onAmountMaxChange={setAmountMax}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateRangeChange={handleDateRangeChange}
      />

      {isLoading && (
        <Card>
          <CardContent className="py-6">
            <TableSkeleton rows={5} cols={7} />
          </CardContent>
        </Card>
      )}

      {error && <ApiErrorDisplay error={error} onRetry={() => refetch()} />}

      {!isLoading && !error && data && (
        <Card>
          {showFilterHint && (
            <div className="border-b px-4 py-2 text-sm text-muted-foreground">
              {t('list.filterHint', {
                shown: filteredItems.length,
                total: totalCount,
                defaultValue: 'Zeige {{shown}} von {{total}} Einträgen auf dieser Seite',
              })}
            </div>
          )}
          <TransactionTable
            transactions={filteredItems}
            onRowClick={handleRowClick}
            sort={sort}
            onToggleSort={toggleSort}
          />
          <CursorPagination
            hasMore={data.pagination.hasMore}
            nextCursor={data.pagination.nextCursor}
            onNext={handleNextPage}
            onPrevious={handlePreviousPage}
            hasPrevious={cursorStack.length > 0}
            isLoading={isLoading}
          />
        </Card>
      )}
    </div>
  );
}
